import { useCallback, useContext, useEffect, useRef } from 'react';
import { FilterContext } from 'components/filter/filters-next/FilterContext';
import { FilterGroupContext } from 'components/filter/filters-next/FilterGroupContext';
import { useDeleteFilter } from 'components/filter/filters-next/useDeleteFilter';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { useGetMutableValues } from 'components/filter/filters-next/useGetMutableValues';
import { FilterFormValue, FilterValue } from 'components/filter/types';
import { useUpdateFiltersByResource } from 'components/filter/useUpdateFiltersByResource';
import { v4 as uuid } from 'uuid';

interface UseFilterProps<T, K = T> {
    formValueToFilter?: (value: K) => T;
}

const useFilter = <T, K = T>(props: UseFilterProps<T, K> = {}) => {
    const { formValueToFilter: _formValueToFilter } = props;
    const formValueToFilterRef = useRef(_formValueToFilter);
    const { forceRender, cacheKey, nodeId, strategyType, relationalServerKey, isLegacy } =
        useContext(FilterContext);
    const { groupId } = useContext(FilterGroupContext);
    const stableUuid = useRef(uuid());
    const nextNodeId = nodeId ?? stableUuid.current;
    const { currentGroup } = useFiltersByKey(cacheKey, groupId);

    const defaultValue = (
        strategyType ? currentGroup?.filters.find(f => f.id === nodeId) : undefined
    ) as FilterValue<T> | undefined;

    const getMutableValues = useGetMutableValues();
    const updateFiltersByResource = useUpdateFiltersByResource();
    const deleteFilter = useDeleteFilter();

    useEffect(() => {
        formValueToFilterRef.current = _formValueToFilter;
    }, [_formValueToFilter]);

    const createFilter = useCallback(
        ({ value, ...values }: FilterFormValue<K>) => {
            const mutableValues = getMutableValues();

            if (mutableValues && strategyType) {
                const nextValue = formValueToFilterRef.current
                    ? formValueToFilterRef.current(value)
                    : value;
                mutableValues.filters.push({
                    id: nextNodeId,
                    serverKey: strategyType,
                    relationalServerKey,
                    value: nextValue,
                    isLegacy,
                    ...values,
                });

                updateFiltersByResource(mutableValues);
                forceRender?.();
            }
        },
        [
            getMutableValues,
            isLegacy,
            nextNodeId,
            relationalServerKey,
            strategyType,
            updateFiltersByResource,
            forceRender,
        ],
    );

    const patchFilter = useCallback(
        ({ value, ...values }: FilterFormValue<K>) => {
            const mutableValues = getMutableValues();
            const index = mutableValues.filters.findIndex(v => v.id === nodeId);
            if (index !== -1) {
                const nextValue = formValueToFilterRef.current
                    ? formValueToFilterRef.current(value)
                    : value;
                mutableValues.filters[index] = {
                    ...mutableValues.filters[index],
                    ...values,
                    value: nextValue,
                };
                updateFiltersByResource(mutableValues);
            }
        },
        [getMutableValues, nodeId, updateFiltersByResource],
    );

    const createOrUpdateFilter = useCallback(
        (values: FilterFormValue<K>) => {
            if (nodeId) {
                patchFilter(values);
            } else {
                createFilter(values);
            }
        },
        [createFilter, nodeId, patchFilter],
    );

    return {
        defaultValue,
        createFilter,
        patchFilter,
        deleteFilter,
        createOrUpdateFilter,
    };
};

export { useFilter };
export type { UseFilterProps };
